import React, { useEffect, useMemo, useState } from "react";
import { executeQuery } from "../../../graphql/client";

// Membership review tab (#323 step 2 — LIST ONLY). Every member of the
// main GroupMe chat, joined to socialUsers on groupMeUserId and
// classified from live signals (Stripe subscription status, Season
// Pass, captains roster / Cognito groups, women-free policy) instead
// of the paid / approved / lastPaidDate latches David has been
// eyeballing by hand:
//   lapsed   subscription over (canceled / unpaid / …) or nothing on
//            record — sorted longest-lapsed first; "in grace" inside
//            the grace window
//   check    the site still grants access but only a latch says so
//            (stale legacy paid date, approved with no payment)
//   unlinked chat member with no socialUsers row carrying their id
//   keep     everyone with a live reason
// There is deliberately NO remove button here (#323 step 3 is a
// separate, later decision). Run this next to the manual check for a
// few weeks; when the two agree, removal can be discussed.

const REVIEW_QUERY = `query MembershipReview($graceDays: Int) {
  membershipReview(graceDays: $graceDays) {
    generatedAt groupId graceDays chatMemberCount
    keep lapsed lapsedInGrace check unlinked warnings
    rows {
      groupMeUserId groupMeName userId name email
      bucket reason reasonText since days inGrace
      lastStatus periodEnd seasonPassUntil lastPaidDate
      cancelAtPeriodEnd approvedFlag rowCount
    }
  }
}`;

const OCEAN = "var(--ab-ocean, #0e7c9b)";
const SEAFOAM = "var(--ab-seafoam, #e7f4f3)";

const BUCKETS = [
  { key: "lapsed", label: "Lapsed", color: "#b00020", bg: "#fdecea" },
  { key: "check", label: "Check", color: "#8a5a00", bg: "#fff4d6" },
  { key: "unlinked", label: "Unlinked", color: "#4a5568", bg: "#edf2f7" },
  { key: "keep", label: "Keep", color: "#1f7a3a", bg: "#e6f4ea" },
];
const bucketMeta = (key) => BUCKETS.find((b) => b.key === key) || BUCKETS[2];

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const card = {
  border: "1px solid #e6e9f0",
  borderRadius: 10,
  background: "#fff",
  padding: "12px 14px",
  marginBottom: 14,
};
const muted = { color: "#6a7385", fontSize: 13 };
const th = {
  textAlign: "left",
  fontSize: 12,
  color: "#6a7385",
  fontWeight: 600,
  padding: "4px 6px",
  borderBottom: "1px solid #e6e9f0",
  whiteSpace: "nowrap",
};
const td = { padding: "6px", fontSize: 14, verticalAlign: "top" };
const input = {
  boxSizing: "border-box",
  padding: "7px 9px",
  border: "1px solid #d7dbe4",
  borderRadius: 6,
  fontSize: 14,
};
const pill = (active, color) => ({
  border: active ? `2px solid ${color || OCEAN}` : "1px solid #d7dbe4",
  background: active ? SEAFOAM : "#fff",
  color: "#333",
  borderRadius: 999,
  padding: "6px 14px",
  fontSize: 13,
  fontWeight: active ? 700 : 500,
  cursor: "pointer",
});
const btn = {
  border: `1px solid ${OCEAN}`,
  background: "#fff",
  color: OCEAN,
  borderRadius: 6,
  padding: "6px 12px",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
};

const Stat = ({ label, value, sub, color }) => (
  <div style={{ ...card, marginBottom: 0, flex: "1 1 120px", padding: "10px 12px" }}>
    <div style={{ fontSize: 12, color: "#6a7385", fontWeight: 600 }}>{label}</div>
    <div style={{ fontSize: 24, fontWeight: 800, color: color || "#1f2330" }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: "#8a93a3" }}>{sub}</div>}
  </div>
);

const Badge = ({ bucket, inGrace }) => {
  const m = bucketMeta(bucket);
  return (
    <span
      style={{
        display: "inline-block",
        background: m.bg,
        color: m.color,
        borderRadius: 999,
        padding: "2px 9px",
        fontSize: 12,
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      {m.label}
      {inGrace ? " · in grace" : ""}
    </span>
  );
};

const MembershipReviewTab = () => {
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [graceDays, setGraceDays] = useState(7);
  const [filter, setFilter] = useState("lapsed");
  const [search, setSearch] = useState("");

  const load = async (grace) => {
    setLoading(true);
    setError("");
    try {
      const out = await executeQuery(REVIEW_QUERY, { graceDays: grace });
      setReview(out?.membershipReview || null);
    } catch (err) {
      setError(err.message || "Couldn't load the membership review.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(7);
  }, []);

  const rows = useMemo(() => (review ? review.rows : []), [review]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter !== "all" && r.bucket !== filter) return false;
      if (!q) return true;
      return [r.name, r.groupMeName, r.email, r.reasonText, r.lastStatus]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [rows, filter, search]);

  const emails = useMemo(
    () => [...new Set(shown.map((r) => r.email).filter(Boolean))],
    [shown]
  );

  const flash = (msg) => {
    setNotice(msg);
    setTimeout(() => setNotice(""), 2500);
  };

  const copyEmails = async () => {
    try {
      await navigator.clipboard.writeText(emails.join(", "));
      flash(`Copied ${emails.length} email${emails.length === 1 ? "" : "s"}.`);
    } catch (err) {
      flash("Couldn't copy — select the list instead.");
    }
  };

  const copyNames = async () => {
    const names = shown.map((r) => r.name || r.groupMeName).filter(Boolean);
    try {
      await navigator.clipboard.writeText(names.join("\n"));
      flash(`Copied ${names.length} name${names.length === 1 ? "" : "s"}.`);
    } catch (err) {
      flash("Couldn't copy — select the list instead.");
    }
  };

  const applyGrace = (e) => {
    e.preventDefault();
    const n = Math.max(0, Math.min(90, Number(graceDays) || 0));
    setGraceDays(n);
    load(n);
  };

  return (
    <div style={{ maxWidth: 1080 }}>
      <h2 style={{ marginTop: 0 }}>
        <span role="img" aria-label="broom">🧹</span> Membership review
      </h2>
      <p style={muted}>
        Everyone in the main GroupMe chat, matched to their site record and
        judged on <strong>live</strong> signals — Stripe subscription status,
        Season Pass, the captains roster, the women-free policy — never on the{" "}
        <code>paid</code> / <code>approved</code> / last-paid-date flags, which
        stay set forever once stamped. <strong>Lapsed</strong> = the
        subscription is over (or nothing is on record). <strong>Check</strong>{" "}
        = the site still grants access but only a stale flag says so.{" "}
        <strong>Unlinked</strong> = a chat member with no site record carrying
        their GroupMe id. This tab only lists; removing someone from the chat is
        still a manual GroupMe action — compare this list with your own check
        for a few weeks first.
      </p>

      {error && (
        <div
          style={{
            background: "#fdecea",
            border: "1px solid #e7b3ba",
            color: "#b00020",
            borderRadius: 8,
            padding: "10px 12px",
            marginBottom: 12,
          }}
        >
          {error}
        </div>
      )}
      {review && review.warnings && review.warnings.length > 0 && (
        <div
          style={{
            background: "#fff4d6",
            border: "1px solid #f0d58c",
            color: "#8a5a00",
            borderRadius: 8,
            padding: "10px 12px",
            marginBottom: 12,
            fontSize: 13,
          }}
        >
          {review.warnings.map((w) => (
            <div key={w}>
              <span role="img" aria-label="warning">⚠️</span> {w}
            </div>
          ))}
        </div>
      )}
      {notice && (
        <div
          style={{
            background: SEAFOAM,
            border: `1px solid ${OCEAN}`,
            color: "#1f2330",
            borderRadius: 8,
            padding: "8px 12px",
            marginBottom: 12,
            fontSize: 13,
          }}
        >
          {notice}
        </div>
      )}

      {loading && !review && <p style={muted}>Reading the chat and the member table…</p>}

      {review && (
        <>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
            <Stat label="In the chat" value={review.chatMemberCount} sub={`as of ${fmtDate(review.generatedAt)}`} />
            <Stat
              label="Lapsed"
              value={review.lapsed}
              color="#b00020"
              sub={review.lapsedInGrace ? `${review.lapsedInGrace} inside the ${review.graceDays}-day grace` : `${review.graceDays}-day grace`}
            />
            <Stat label="Check" value={review.check} color="#8a5a00" sub="latch only, your call" />
            <Stat label="Unlinked" value={review.unlinked} color="#4a5568" sub="no site record" />
            <Stat label="Keep" value={review.keep} color="#1f7a3a" />
          </div>

          <div style={{ ...card, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {BUCKETS.map((b) => (
                <button
                  key={b.key}
                  type="button"
                  style={pill(filter === b.key, b.color)}
                  onClick={() => setFilter(b.key)}
                >
                  {b.label} ({review[b.key]})
                </button>
              ))}
              <button type="button" style={pill(filter === "all")} onClick={() => setFilter("all")}>
                All ({review.chatMemberCount})
              </button>
            </div>
            <input
              style={{ ...input, flex: "1 1 160px", minWidth: 140 }}
              placeholder="Search name, email, reason…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <form onSubmit={applyGrace} style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <label htmlFor="mr-grace" style={{ ...muted, whiteSpace: "nowrap" }}>
                Grace days
              </label>
              <input
                id="mr-grace"
                type="number"
                min={0}
                max={90}
                style={{ ...input, width: 64 }}
                value={graceDays}
                onChange={(e) => setGraceDays(e.target.value)}
              />
              <button type="submit" style={btn} disabled={loading}>
                {loading ? "Loading…" : "Refresh"}
              </button>
            </form>
            <button type="button" style={btn} onClick={copyEmails} disabled={emails.length === 0}>
              Copy {emails.length} email{emails.length === 1 ? "" : "s"}
            </button>
            <button type="button" style={btn} onClick={copyNames} disabled={shown.length === 0}>
              Copy names
            </button>
          </div>

          {shown.length === 0 ? (
            <p style={muted}>
              {filter === "lapsed"
                ? "Nobody lapsed. 🎉"
                : "Nothing in this bucket" + (search ? " matching your search." : ".")}
            </p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ borderCollapse: "collapse", width: "100%" }}>
                <thead>
                  <tr>
                    <th style={th}>Status</th>
                    <th style={th}>In the chat as</th>
                    <th style={th}>Site record</th>
                    <th style={th}>Why</th>
                    <th style={th}>Since</th>
                    <th style={th}>Days</th>
                    <th style={th}>Stripe</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r) => (
                    <tr key={r.groupMeUserId} style={{ borderBottom: "1px solid #f0f2f6" }}>
                      <td style={td}>
                        <Badge bucket={r.bucket} inGrace={r.inGrace} />
                      </td>
                      <td style={td}>
                        <div style={{ fontWeight: 600 }}>{r.groupMeName}</div>
                        <div style={{ ...muted, fontSize: 11 }}>GroupMe {r.groupMeUserId}</div>
                      </td>
                      <td style={td}>
                        {r.userId ? (
                          <>
                            <div>{r.name || <span style={muted}>(no name)</span>}</div>
                            {r.email && <div style={{ ...muted, wordBreak: "break-all" }}>{r.email}</div>}
                            {r.rowCount > 1 && (
                              <div style={{ ...muted, fontSize: 11 }}>{r.rowCount} rows — best shown</div>
                            )}
                          </>
                        ) : (
                          <span style={muted}>—</span>
                        )}
                      </td>
                      <td style={{ ...td, maxWidth: 360 }}>
                        {r.reasonText}
                        {r.approvedFlag && r.bucket === "lapsed" && (
                          <div style={{ color: "#8a5a00", fontSize: 12 }}>
                            approved flag is still set — the site still treats them as a member
                          </div>
                        )}
                      </td>
                      <td style={{ ...td, whiteSpace: "nowrap" }}>{fmtDate(r.since) || <span style={muted}>—</span>}</td>
                      <td style={{ ...td, textAlign: "right" }}>{r.days == null ? "" : r.days}</td>
                      <td style={{ ...td, whiteSpace: "nowrap", fontSize: 12 }}>
                        <div>{r.lastStatus || "—"}{r.cancelAtPeriodEnd ? " · cancels at end" : ""}</div>
                        {r.periodEnd && <div style={muted}>period end {fmtDate(r.periodEnd)}</div>}
                        {r.seasonPassUntil && <div style={muted}>pass until {fmtDate(r.seasonPassUntil)}</div>}
                        {r.lastPaidDate && <div style={muted}>last paid {fmtDate(r.lastPaidDate)}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p style={{ ...muted, marginTop: 14 }}>
            Group {review.groupId || "?"} · grace {review.graceDays} days · lapsed
            rows count from the Stripe period end (or the last paid date when
            there is none); "check" rows from the last paid or join date. A
            captain is anyone on the Captains roster or in the Captains Cognito
            group; an admin is anyone in the Admins group.
          </p>
        </>
      )}
    </div>
  );
};

export default MembershipReviewTab;
